/**
 * Veercal — cost-breakdown line-item contract.
 * ─────────────────────────────────────────────────────────────────────────────
 * Provides a unified `LineItem` shape that the bento / depth-3 reveal layer
 * can render uniformly across the five finance methods (cash, personal loan,
 * dealer finance, finance lease, novated lease) and, in a follow-up, across
 * the three standalone calculators (novated.html, end-of-term.html,
 * stamp-duty.html).
 *
 * THIS FILE DOES NOT MODIFY ANY MATHS. Every contributor is read from the
 * existing engine (calcCash/calcLoan/calcLease/calcNovated in App.js, plus
 * running/stampDutyForDisplay/pmt/etc.). Where the result object stores
 * rounded values (e.g. monthlyPayment), the builder re-computes the
 * unrounded equivalent via the same engine helpers so the line-item sum
 * reconciles to the rounded totalCost within $1.
 *
 * ─── LineItem contract ──────────────────────────────────────────────────
 *   {
 *     key:     string,          // stable id ('upfrontCost', 'stampDuty', …)
 *     label:   string,          // human-readable display label
 *     value:   number,          // unrounded magnitude (always >= 0)
 *     sign:    '+' | '-',       // direction toward true total cost
 *     working: string,          // derivation built with fmt() from real
 *                               // engine values — e.g. "$42,000 × 4.0%
 *                               // (VIC) = $1,680"
 *   }
 *
 * Every builder appends a terminal `{ key: 'total', … }` row whose value is
 * `result.totalCost` (rounded, matches what the UI displays).
 *
 * Invariant per builder (checked by `verify(items)`):
 *   abs( signedSum(items) - items.find(i=>i.key==='total').value ) < 1
 * where signedSum = Σ (sign === '+' ? value : -value), excluding 'total'.
 * ─────────────────────────────────────────────────────────────────────────
 */

import {
  running,
  stampDutyForDisplay,
  baseFromDriveAway,
  effPrice,
  vehVal,
  sdVType,
  calcLCT,
  pmt,
  buildAmort,
} from '../App';
import {
  FBT_STATUTORY,
  GST_PURCHASE_CREDIT_CAP,
  isEvFbtExempt,
  incomeTax,
  medicareLevy,
} from '../veercal.oncosts';

/* Local mirror of App.js's fmt() (line 534). Single-use, three lines —
   not worth exporting from App.js as a one-off. */
const fmt = (n) =>
  n == null
    ? '–'
    : (n < 0 ? '-$' : '$') +
      Math.abs(Math.round(n)).toLocaleString('en-AU');
const fmtPct = (n, dp = 1) => `${n.toFixed(dp)}%`;

/* ─── Helpers ──────────────────────────────────────────────────────────── */

/**
 * Sum item values by sign, excluding the terminal 'total' row.
 * @param {LineItem[]} items
 * @returns {number}
 */
export function signedSum(items) {
  return items
    .filter((it) => it.key !== 'total')
    .reduce(
      (s, it) => s + (it.sign === '+' ? it.value : -it.value),
      0,
    );
}

/**
 * True iff sum-of-items reconciles to the 'total' row within $1.
 * @param {LineItem[]} items
 * @param {number} [epsilon=1]
 * @returns {boolean}
 */
export function verify(items, epsilon = 1) {
  const total = items.find((it) => it.key === 'total');
  if (!total) return false;
  return Math.abs(signedSum(items) - total.value) < epsilon;
}

/* Build the terminal 'total' item from a method result object. */
function totalItem(result) {
  return {
    key: 'total',
    label: 'True total cost',
    value: result.totalCost,
    sign: '+',
    working: fmt(result.totalCost),
  };
}

/* Shared stamp-duty line item (used by cash + loan + dealer). The rate is
   back-solved from the amount and the base, giving the effective blended
   rate. For tiered states (VIC/NSW above thresholds), this reads as one
   number rather than spelling out each tier — clearer for the depth-2
   reveal. Per-tier detail can be a follow-up. */
function stampDutyItem(d) {
  const base = baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d));
  const amount = stampDutyForDisplay(d);
  const effRate = base > 0 ? (amount / base) * 100 : 0;
  return {
    key: 'stampDuty',
    label: 'Stamp duty',
    value: amount,
    sign: '+',
    working: `${fmt(base)} × ${fmtPct(effRate)} (${d.state}) = ${fmt(amount)}`,
  };
}

/* ─── Per-method decompositions ────────────────────────────────────────── */

function itemsCash(_result, d) {
  const ep = effPrice(d);
  const run = running(d);
  const yrs = d.holdYears;
  const base = baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d));
  const ev = vehVal(base, yrs, d);
  const oppCost = ep * (Math.pow(1 + d.opportunityCostPct, yrs) - 1);
  const rebatePart =
    d.isEV && d.evGovtRebate
      ? ` − ${fmt(d.evGovtRebate)} EV rebate`
      : '';
  return [
    {
      key: 'upfrontCost',
      label: 'Upfront cost (drive-away)',
      value: ep,
      sign: '+',
      working: `${fmt(d.vehiclePrice)} drive-away${rebatePart} = ${fmt(ep)}`,
    },
    {
      key: 'runningTotal',
      label: 'Running costs',
      value: run * yrs,
      sign: '+',
      working: `${fmt(run)} / yr × ${yrs} yrs = ${fmt(run * yrs)}`,
    },
    {
      key: 'opportunityCost',
      label: 'Opportunity cost',
      value: oppCost,
      sign: '+',
      working: `${fmt(ep)} compounded at ${fmtPct(
        d.opportunityCostPct * 100,
      )} for ${yrs} yrs = ${fmt(oppCost)}`,
    },
    {
      key: 'exitValue',
      label: 'Exit value (sale price)',
      value: ev,
      sign: '-',
      working: `${fmt(d.vehiclePrice)} → ${fmt(ev)} at year ${yrs}`,
    },
  ];
}

function itemsLoan(result, d) {
  const isD = result.method === 'dealer';
  const base = baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d));
  const rate = isD ? d.dealerRate : d.loanRate;
  const termYrs = isD ? d.dealerTermYears : d.loanTermYears;
  const dep = isD ? d.dealerDeposit : d.loanDeposit;
  const balloon = isD ? base * d.balloonPct : 0;
  const sd = stampDutyForDisplay(d);
  const lct = calcLCT(base, d.isEV);
  const financedSd = d.financeStampDuty ? sd : 0;
  const upfrontSd = d.financeStampDuty ? 0 : sd;
  const principal = Math.max(0, base + lct + financedSd - dep);
  const months = termYrs * 12;
  /* Match calcLoan amortisation exactly (refi handled below). */
  let amort = buildAmort(principal, rate, months, balloon);
  let monthlyPaymentRefi = null;
  let refinanceMonth = null;
  if (d.refinanceYear > 0 && d.refinanceYear < termYrs) {
    refinanceMonth = d.refinanceYear * 12;
    const balRf = amort.rows[refinanceMonth - 1].balance;
    const a2 = buildAmort(
      balRf,
      d.refinanceRate,
      months - refinanceMonth,
      balloon,
    );
    monthlyPaymentRefi = a2.monthlyPayment;
  }
  const { monthlyPayment, rows } = amort;
  const hold = d.holdYears;
  const monthsHeld = Math.min(hold * 12, months);
  const cumPay =
    monthlyPaymentRefi == null
      ? monthsHeld * monthlyPayment
      : refinanceMonth * monthlyPayment +
        (monthsHeld - refinanceMonth) * monthlyPaymentRefi;
  const lb =
    hold * 12 <= months
      ? rows[monthsHeld - 1].balance
      : isD
        ? balloon
        : 0;
  const run = running(d);
  const ev = vehVal(base, hold, d);
  const upfront = dep + upfrontSd;
  const oppCost = upfront * (Math.pow(1 + d.opportunityCostPct, hold) - 1);

  const financedExtras = [];
  if (lct > 0) financedExtras.push(`${fmt(lct)} LCT`);
  if (financedSd > 0) financedExtras.push(`${fmt(financedSd)} financed SD`);
  const principalNote = financedExtras.length
    ? ` (incl. ${financedExtras.join(' + ')})`
    : '';
  const monthlyWorking =
    monthlyPaymentRefi == null
      ? `${fmt(monthlyPayment)} / mo × ${monthsHeld} mo${principalNote} = ${fmt(cumPay)}`
      : `${fmt(monthlyPayment)} × ${refinanceMonth} mo + ${fmt(
          monthlyPaymentRefi,
        )} × ${monthsHeld - refinanceMonth} mo${principalNote} = ${fmt(cumPay)}`;

  const items = [
    {
      key: 'deposit',
      label: 'Deposit',
      value: dep,
      sign: '+',
      working: fmt(dep),
    },
  ];
  if (upfrontSd > 0) items.push(stampDutyItem(d));
  items.push({
    key: 'monthlyPaymentsTotal',
    label: 'Monthly payments',
    value: cumPay,
    sign: '+',
    working: monthlyWorking,
  });
  items.push({
    key: 'runningTotal',
    label: 'Running costs',
    value: run * hold,
    sign: '+',
    working: `${fmt(run)} / yr × ${hold} yrs = ${fmt(run * hold)}`,
  });
  if (lb > 0) {
    items.push({
      key: 'remainingBalance',
      label:
        isD && hold >= termYrs
          ? 'Balloon owing at term end'
          : `Loan balance at year ${hold}`,
      value: lb,
      sign: '+',
      working: fmt(lb),
    });
  }
  if (oppCost > 0) {
    items.push({
      key: 'opportunityCost',
      label: 'Opportunity cost (on upfront)',
      value: oppCost,
      sign: '+',
      working: `${fmt(upfront)} upfront compounded at ${fmtPct(
        d.opportunityCostPct * 100,
      )} for ${hold} yrs = ${fmt(oppCost)}`,
    });
  }
  items.push({
    key: 'exitValue',
    label: 'Exit value (sale price)',
    value: ev,
    sign: '-',
    working: `${fmt(d.vehiclePrice)} → ${fmt(ev)} at year ${hold}`,
  });
  return items;
}

function itemsLease(_result, d) {
  /* calcLease formula (post-C5 fix): totalCost = tl + run * runYears, where
       tl       = min(yrs, leaseTermYears) * 12 * monthlyPayment
       runYears = min(yrs, leaseTermYears)
     Running costs stop at lease end — once the user returns the car they
     have no vehicle (unlike a novated lease where they buy out the residual).
     The lease residual is the financier's exit price, NOT the user's payment,
     so it does not appear in totalCost — surfaced informationally below. */
  const months = d.leaseTermYears * 12;
  const res = d.vehiclePrice * d.leaseResidualPct;
  const { monthlyPayment } = buildAmort(
    d.vehiclePrice,
    d.leaseRate,
    months,
    res,
  );
  const yrs = d.holdYears;
  const run = running(d);
  const runYears = Math.min(yrs, d.leaseTermYears);
  const monthsHeld = runYears * 12;
  const tl = monthsHeld * monthlyPayment;
  const totalRunning = run * runYears;
  return [
    {
      key: 'monthlyPaymentsTotal',
      label: 'Lease payments',
      value: tl,
      sign: '+',
      working: `${fmt(monthlyPayment)} / mo × ${monthsHeld} mo on ${fmt(
        d.vehiclePrice,
      )} with ${fmt(res)} residual = ${fmt(tl)}`,
    },
    {
      key: 'runningTotal',
      label: 'Running costs',
      value: totalRunning,
      sign: '+',
      working: `${fmt(run)} / yr × ${runYears} lease yrs = ${fmt(totalRunning)}`,
    },
  ];
}

function itemsNovated(_result, d) {
  /* Match calcNovated's internal derivation exactly (App.js:433-516). */
  const base = baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d));
  const evExempt = isEvFbtExempt(d.vehiclePrice, sdVType(d), d.state);
  const leaseMos = d.novatedTermYears * 12;
  const lct = calcLCT(base, d.isEV);
  const fbtCostPrice = base + lct;
  const res = fbtCostPrice * d.novatedResidualPct;
  /* Purchase GST credit — mirrors calcNovated (App.js): financier funds the lease
     ex-GST, credit capped at 1/11 of the ATO car limit. */
  const purchaseGstCredit = d.novatedGstOnPurchase
    ? Math.min(base / 11, GST_PURCHASE_CREDIT_CAP)
    : 0;
  const principal = d.vehiclePrice - purchaseGstCredit;
  const gf = pmt(d.novatedRate, leaseMos, principal, -res);
  const run = running(d);
  const runMo = run / 12;
  const providerFeeAnnual = d.novatedProviderFeeAnnual || 0;
  const providerFeeMo = providerFeeAnnual / 12;
  const preTax = gf + runMo + providerFeeMo;
  const annualSacrifice = preTax * 12;
  const totalTax = (s) => incomeTax(s) + medicareLevy(s);
  const saving =
    (totalTax(d.grossSalary) -
      totalTax(Math.max(0, d.grossSalary - annualSacrifice))) /
    12;
  const fbtMo = evExempt ? 0 : (fbtCostPrice * FBT_STATUTORY) / 12;
  const gstApplicableAnnual =
    Math.max(0, run - d.regPerYear) + providerFeeAnnual;
  const gstCreditMo = gstApplicableAnnual / 11 / 12;
  const yrs = d.holdYears;
  const exitValue = vehVal(base, yrs, d);
  const postLeaseRunning = run * Math.max(0, yrs - d.novatedTermYears);

  const items = [
    {
      key: 'preTaxFinanceTotal',
      label: 'Finance payments (pre-tax)',
      value: gf * leaseMos,
      sign: '+',
      working: `${fmt(gf)} / mo × ${leaseMos} mo on ${fmt(principal)} financed (${
        purchaseGstCredit > 0
          ? `${fmt(d.vehiclePrice)} drive-away − ${fmt(
              purchaseGstCredit,
            )} GST credit on purchase`
          : `${fmt(d.vehiclePrice)} drive-away`
      }) with ${fmt(res)} residual = ${fmt(gf * leaseMos)}`,
    },
    {
      key: 'runningTotalLease',
      label: 'Running costs (lease period, pre-tax)',
      value: runMo * leaseMos,
      sign: '+',
      working: `${fmt(run)} / yr × ${d.novatedTermYears} yrs = ${fmt(
        runMo * leaseMos,
      )}`,
    },
  ];
  if (providerFeeAnnual > 0) {
    items.push({
      key: 'providerFeeTotal',
      label: 'Provider fee (pre-tax)',
      value: providerFeeMo * leaseMos,
      sign: '+',
      working: `${fmt(providerFeeAnnual)} / yr × ${d.novatedTermYears} yrs = ${fmt(
        providerFeeMo * leaseMos,
      )}`,
    });
  }
  items.push({
    key: 'taxSavingTotal',
    label: 'Income tax saving',
    value: saving * leaseMos,
    sign: '-',
    working: `${fmt(saving)} / mo × ${leaseMos} mo (sacrifice ${fmt(
      annualSacrifice,
    )}/yr at marginal rate) = ${fmt(saving * leaseMos)}`,
  });
  if (!evExempt) {
    items.push({
      key: 'fbtTotal',
      label: 'FBT (ECM contribution)',
      value: fbtMo * leaseMos,
      sign: '+',
      working: `${fmt(fbtCostPrice)} cost price × ${fmtPct(
        FBT_STATUTORY * 100,
      )} statutory ÷ 12 × ${leaseMos} mo = ${fmt(fbtMo * leaseMos)}`,
    });
  }
  if (gstCreditMo > 0) {
    items.push({
      key: 'gstCreditTotal',
      label: 'GST input credit',
      value: gstCreditMo * leaseMos,
      sign: '-',
      working: `${fmt(gstApplicableAnnual)} GST-incl. running ÷ 11 × ${
        d.novatedTermYears
      } yrs = ${fmt(gstCreditMo * leaseMos)}`,
    });
  }
  items.push({
    key: 'residualBuyout',
    label: 'Residual buyout',
    value: res,
    sign: '+',
    working: `${fmt(fbtCostPrice)} FBT cost price × ${fmtPct(
      d.novatedResidualPct * 100,
    )} = ${fmt(res)}`,
  });
  if (postLeaseRunning > 0) {
    items.push({
      key: 'postLeaseRunning',
      label: 'Post-lease running costs',
      value: postLeaseRunning,
      sign: '+',
      working: `${fmt(run)} / yr × ${
        yrs - d.novatedTermYears
      } yrs (post-term) = ${fmt(postLeaseRunning)}`,
    });
  }
  items.push({
    key: 'exitValue',
    label: 'Exit value (sale price)',
    value: exitValue,
    sign: '-',
    working: `${fmt(d.vehiclePrice)} → ${fmt(exitValue)} at year ${yrs}`,
  });
  return items;
}

/* ─── Public builders ──────────────────────────────────────────────────── */

/**
 * Build the cost-comparison line items for ONE method's result.
 * Called per method (cash | loan | dealer | lease | novated).
 *
 * @param {object} result - return value from calcCash/calcLoan/etc. — has
 *   `method` and `totalCost` at minimum.
 * @param {object} inputs - user-facing inputs (reserved; equal to `d` in
 *   App.js today, separated by the brief for future evolution).
 * @param {object} d - normalised inputs passed to the maths engine.
 * @returns {LineItem[]} — N+1 items, terminal item is the 'total' row.
 */
// eslint-disable-next-line no-unused-vars
export function buildCostComparison(result, inputs, d) {
  let items;
  switch (result.method) {
    case 'cash':
      items = itemsCash(result, d);
      break;
    case 'loan':
    case 'dealer':
      items = itemsLoan(result, d);
      break;
    case 'lease':
      items = itemsLease(result, d);
      break;
    case 'novated':
      items = itemsNovated(result, d);
      break;
    default:
      throw new Error(
        `buildCostComparison: unknown method "${result.method}"`,
      );
  }
  items.push(totalItem(result));
  return items;
}

/* ─── Stubs ────────────────────────────────────────────────────────────── */
/* The three standalone HTML calculators (novated.html, end-of-term.html,
   stamp-duty.html) compute results via DOM updates rather than returning
   structured objects. Filling these in requires either porting those
   calculators into React or refactoring their inline calc() functions to
   return `{ totalCost, … }` objects. Until then, these stubs document the
   contract and throw a clear "not implemented" error. */

/**
 * Stub. Source: public/novated.html `calc()` at lines 1022-1227 (computes
 * monthlyPmt, fbtPayable, ecmContrib, annualSacrifice, annualTaxSaving,
 * totalCostNovated via inline DOM updates).
 */
// eslint-disable-next-line no-unused-vars
export function buildNovated(result, inputs, d) {
  throw new Error(
    'buildNovated: not implemented; see public/novated.html calc()',
  );
}

/**
 * Stub. Source: public/end-of-term.html `calc()` at lines 614-812.
 */
// eslint-disable-next-line no-unused-vars
export function buildEndOfTerm(result, inputs, d) {
  throw new Error(
    'buildEndOfTerm: not implemented; see public/end-of-term.html calc()',
  );
}

/**
 * Stub. Source: public/stamp-duty.html `calculate()` at lines 1357-1495.
 */
// eslint-disable-next-line no-unused-vars
export function buildStampDuty(result, inputs, d) {
  throw new Error(
    'buildStampDuty: not implemented; see public/stamp-duty.html calculate()',
  );
}
